import React, { useState } from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  Container,
  Button,
  Input,
  HStack,
  useColorModeValue,
  useBreakpointValue,
  Icon,
  Badge,
  Fade,
  ScaleFade,
  useToast,
  InputGroup,
  InputRightElement,
  Spinner
} from '@chakra-ui/react';
import { FaGlobe, FaCheck, FaTimes, FaRocket, FaSearch } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axios from 'axios';

const OwnADomain = () => {
  const API_BASE_URL = process.env.NODE_ENV === 'development' ? process.env.REACT_APP_API_BASE_URL_DEV : process.env.REACT_APP_API_BASE_URL_PROD;
  const [domain, setDomain] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [isAvailable, setIsAvailable] = useState(null);
  const [sanitizedDomain, setSanitizedDomain] = useState('');
  const toast = useToast();

  const textColor = useColorModeValue('gray.800', 'gray.100');
  const accentColor = useColorModeValue('yellow.400', 'yellow.300');
  const cardBgColor = useColorModeValue('white', 'gray.700');

  const headingSize = useBreakpointValue({ base: 'xl', md: '2xl' });

  const sanitizeSubdomain = (name) => {
    return name
      .toLowerCase()
      // Replace special characters with user-friendly alternatives
      .replace(/@/g, '-at-')
      .replace(/&/g, '-and-')
      // Replace other illegal characters with a hyphen
      .replace(/[^a-z0-9-]+/g, '-')
      // Trim hyphens from start/end
      .replace(/^-+|-+$/g, '')
      // Limit to 63 characters (max subdomain length)
      .substring(0, 63);
  };

  const checkDomainAvailability = async () => {
    if (!domain.trim()) {
      toast({
        title: 'Please enter a domain name',
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
      return;
    }

    setIsChecking(true);
    const sanitized = sanitizeSubdomain(domain);
    setSanitizedDomain(sanitized);

    try {
      // Make API call to backend to check subdomain availability
      const response = await axios.post(
        `${API_BASE_URL}/api/landingAuth/check-subdomain-availability`,
        { subdomain: sanitized }
      );

      if (response.status === 200) {
        setIsAvailable(true);
        toast({
          title: 'Great news!',
          description: `${sanitized}.piepos.in is available!`,
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top'
        });
      }
    } catch (error) {
      if (error.response && error.response.status === 400) {
        // Subdomain is already taken
        setIsAvailable(false);
        toast({
          title: 'Domain not available',
          description: 'Try a different name or variation',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top'
        });
      } else {
        // Server error or other issues
        setIsAvailable(false);
        toast({
          title: 'Error checking domain',
          description: 'Please try again later',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top'
        });
        console.error('Error checking domain:', error);
      }
    } finally {
      setIsChecking(false);
    }
  };

  const handleInputChange = (e) => {
    setDomain(e.target.value);
    // Reset availability status when user types
    if (isAvailable !== null) {
      setIsAvailable(null);
    }
  };

  return (
    <Box minH="100vh" position="relative" overflow="hidden" marginBottom="10">
      {/* Mobile decorations */}
      <Box
        display={{ base: 'block', md: 'none' }}
        position="absolute"
        top="10%"
        right="10%"
        w="50px"
        h="50px"
        bg="blue.200"
        borderRadius="50%"
        opacity="0.6"
        zIndex="0"
        animation="bounce 3s ease-in-out infinite"
        sx={{
          '@keyframes bounce': {
            '0%, 100%': { transform: 'translateY(0px)' },
            '50%': { transform: 'translateY(-10px)' }
          }
        }}
      />

      <Box
        display={{ base: 'block', md: 'none' }}
        position="absolute"
        bottom="15%"
        left="8%"
        w="35px"
        h="35px"
        bg={accentColor}
        borderRadius="50% 50% 0 50%"
        opacity="0.7"
        zIndex="0"
        animation="wiggle 4s ease-in-out infinite"
        sx={{
          '@keyframes wiggle': {
            '0%, 100%': { transform: 'rotate(0deg)' },
            '50%': { transform: 'rotate(15deg)' }
          }
        }}
      />

      {/* Gradient Overlays */}
      <Container
        maxW="7xl"
        position="relative"
        zIndex="1"
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        py={{ base: 10, md: 0 }}
      >
        <VStack spacing={{ base: 8, md: 12 }} px={{ base: 4, md: 0 }}>

          {/* Header Section */}
          <VStack spacing={{ base: 4, md: 6 }} textAlign="center" maxW="4xl">
            <Heading
              as="h2"
              size={headingSize}
              color={textColor}
              fontWeight="bold"
              lineHeight="1.2"
            >
              Get Your Custom Domain
            </Heading>

            <Box position="relative">
              <Text
                fontSize={{ base: 'md', md: 'lg' }}
                color="gray.600"
                maxW="3xl"
                fontWeight="medium"
                lineHeight="1.6"
              >
                Stand out with {' '}
                <Box as="span" position="relative" display="inline-block">
                  your own branded domain
                  {/* Doodle underline */}
                  <Box
                    position="absolute"
                    bottom={-1}
                    left="0"
                    w="100%"
                    h="3px"
                    bg="blue.400"
                    borderRadius="full"
                    transform="rotate(-1deg)"
                    opacity={0.8}
                  />
                </Box>
                . Build trust with customers and create a professional online presence.
              </Text>
            </Box>
          </VStack>

          {/* Domain Checker Card */}
          <Box
            bg={cardBgColor}
            p={{ base: 6, md: 8 }}
            borderRadius="2xl"
            border="2px solid"
            borderColor="blue.200"
            boxShadow="xl"
            maxW="2xl"
            w="full"
            position="relative"
            overflow="hidden"
          >
            {/* Card decoration */}
            <Box
              position="absolute"
              top="-40px"
              right="-40px"
              w="120px"
              h="120px"
              bg="blue.50"
              borderRadius="50%"
              opacity="0.5"
              zIndex="0"
            />

            <VStack spacing={6} position="relative" zIndex="1">
              <VStack spacing={2}>
                <Icon as={FaGlobe} boxSize="12" color="blue.500" />
                <Text fontSize="lg" color={textColor} fontWeight="bold">
                  Check Domain Availability
                </Text>
                <Text fontSize="sm" color="gray.500" textAlign="center">
                  Enter your restaurant name to see if it's available
                </Text>
              </VStack>

              <VStack spacing={4} w="full">
                <HStack w="full" spacing={2}>
                  <InputGroup flex="1">
                    <Input
                      placeholder="yourrestaurant"
                      value={domain}
                      onChange={handleInputChange}
                      bg="gray.50"
                      borderColor="gray.300"
                      focusBorderColor="blue.500"
                      size="lg"
                      borderRadius="lg"
                      _placeholder={{ color: 'gray.400' }}
                    />
                    {isChecking && (
                      <InputRightElement height="100%">
                        <Spinner size="sm" color="blue.500" />
                      </InputRightElement>
                    )}
                  </InputGroup>
                  <Text fontSize="lg" color="gray.600" fontWeight="medium" whiteSpace="nowrap">
                    .piepos.in
                  </Text>
                </HStack>

                <Button
                  size="lg"
                  bg="blue.500"
                  color="white"
                  _hover={{ bg: 'blue.600', transform: 'translateY(-2px)' }}
                  _active={{ bg: 'blue.700' }}
                  width="full"
                  borderRadius="lg"
                  leftIcon={<FaSearch />}
                  onClick={checkDomainAvailability}
                  isLoading={isChecking}
                  loadingText="Checking..."
                  transition="all 0.3s ease"
                >
                  Check Availability
                </Button>

                <Button
                  as={Link}
                  to="/login"
                  size="lg"
                  bg="gray.600"
                  color="white"
                  _hover={{bg:'gray.700', transform:'translateY(-2px)'}}
                  _active={{bg:'gray.800'}}
                  width="full"
                  borderRadius="lg"
                  transition="all 0.3s ease"
                >
                    Already have an account? Login
                </Button>
              </VStack>

              {/* Domain Status Display */}
              {isAvailable !== null && (
                <ScaleFade initialScale={0.9} in={isAvailable !== null}>
                  <VStack spacing={4} w="full">
                    {isAvailable ? (
                      <Box
                        bg="green.50"
                        p={4}
                        borderRadius="xl"
                        border="2px solid"
                        borderColor="green.200"
                        w="full"
                      >
                        <VStack spacing={3}>
                          <HStack spacing={2}>
                            <Icon as={FaCheck} color="green.500" boxSize="5" />
                            <Text fontSize="md" fontWeight="bold" color="green.700">
                              Available!
                            </Text>
                          </HStack>
                          <Text fontSize="sm" color="green.600" textAlign="center">
                            <Text as="b">{sanitizedDomain}.piepos.in</Text> is available for you
                          </Text>

                          <Fade in={isAvailable === true}>
                            <Button
                              as={Link}
                              to="/register"
                              size="lg"
                              bg="green.500"
                              color="white"
                              _hover={{ bg: 'green.600', transform: 'translateY(-3px)' }}
                              _active={{ bg: 'green.700' }}
                              // leftIcon={<FaRocket />}
                              borderRadius="lg"
                              boxShadow="lg"
                              transition="all 0.3s ease"
                              animation="glow 2s ease-in-out infinite alternate"
                              sx={{
                                '@keyframes glow': {
                                  '0%': { boxShadow: '0 0 5px rgba(34, 197, 94, 0.5)' },
                                  '100%': { boxShadow: '0 0 20px rgba(34, 197, 94, 0.8)' }
                                }
                              }}
                            >
                              Start Your Journey!
                            </Button>
                          </Fade>
                        </VStack>
                      </Box>
                    ) : (
                      <Box
                        bg="red.50"
                        p={4}
                        borderRadius="xl"
                        border="2px solid"
                        borderColor="red.200"
                        w="full"
                      >
                        <VStack spacing={2}>
                          <HStack spacing={2}>
                            <Icon as={FaTimes} color="red.500" boxSize="5" />
                            <Text fontSize="md" fontWeight="bold" color="red.700">
                              Not Available
                            </Text>
                          </HStack>
                          <Text fontSize="sm" color="red.600" textAlign="center">
                            Try a different name or add numbers/words
                          </Text>
                        </VStack>
                      </Box>
                    )}
                  </VStack>
                </ScaleFade>
              )}
            </VStack>
          </Box>

          {/* Pricing Note */}
          <Box
            bg="yellow.50"
            p={4}
            borderRadius="lg"
            boxShadow="md"
            border="2px solid"
            borderColor="yellow.200"
            maxW="md"
            w="full"
            mx="auto"
          >
            <VStack spacing={4}>
              <Badge colorScheme="yellow" fontSize="md" px={4} py={2} borderRadius="full">
                Limited Time Offer
              </Badge>
              <Text fontSize="sm" color="gray.500" textAlign="center">
                Custom domains start at{' '}
                <Text as="s" display="inline" color="gray.400">
                  ₹1,999/year
                </Text>{' '}
                <Text as="b" color="green.600" fontSize="lg">
                  ₹0/year
                </Text>
              </Text>
              <Text fontSize="xs" color="gray.400" textAlign="center">
                Domain, hosting, and maintenance - all covered by us
              </Text>
              <Button
                as={Link}
                to="/register"
                size="md"
                bg="blue.500"
                color="white"
                _hover={{ bg: 'blue.600', transform: 'translateY(-2px)' }}
                _active={{ bg: 'blue.700' }}
                borderRadius="lg"
                leftIcon={<FaRocket />}
                transition="all 0.3s ease"
                width="full"
              >
                Register Now
              </Button>
            </VStack>
          </Box>

        </VStack>
      </Container>
    </Box>
  );
};

export default OwnADomain;